'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ColorType,
  createChart,
  createSeriesMarkers,
  LineSeries,
  PriceScaleMode,
  type IChartApi,
  type ISeriesApi,
  type LineData,
  type Time,
  type WhitespaceData,
} from 'lightweight-charts';

import { PIZZA_DAY_DATE } from '@/lib/constants';
import type { ChartScale, PizzaSeriesPoint } from '@/lib/types';

type Props = {
  points: PizzaSeriesPoint[];
  metricLabel: string;
  scale: ChartScale;
  isLoading: boolean;
  error: string | null;
};

type TooltipState = {
  date: string;
  value: number;
} | null;

function formatDateFromTime(time?: Time): string {
  if (!time) return '';
  if (typeof time === 'string') return time;
  if (typeof time === 'number') return new Date(time * 1000).toISOString().slice(0, 10);
  return `${time.year}-${String(time.month).padStart(2, '0')}-${String(time.day).padStart(2, '0')}`;
}

function shiftIsoDate(dateIso: string, deltaDays: number): string {
  const d = new Date(`${dateIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

export function PizzaChart({ points, metricLabel, scale, isLoading, error }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const glowSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const seriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState>(null);

  const data = useMemo<Array<LineData<Time> | WhitespaceData<Time>>>(() => {
    const seriesData = points.map((p) => ({ time: p.date as Time, value: Number(p.value.toFixed(8)) }));

    if (!seriesData.length) {
      return seriesData;
    }

    const leftPaddingDate = shiftIsoDate(PIZZA_DAY_DATE, -90);

    return [
      { time: leftPaddingDate as Time },
      { time: PIZZA_DAY_DATE as Time },
      ...seriesData,
    ];
  }, [points]);

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#cdd6e5',
      },
      grid: {
        vertLines: { color: '#332e2c' },
        horzLines: { color: '#332e2c' },
      },
      rightPriceScale: {
        borderVisible: false,
      },
      timeScale: {
        borderVisible: false,
      },
      crosshair: {
        mode: 1,
      },
    });

    const glowSeries = chart.addSeries(LineSeries, {
      color: 'rgba(231, 161, 79, 0.28)',
      lineWidth: 4,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    const series = chart.addSeries(LineSeries, {
      color: '#e7a14f',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    chartRef.current = chart;
    glowSeriesRef.current = glowSeries;
    seriesRef.current = series;

    const ro = new ResizeObserver(() => {
      chart.timeScale().fitContent();
    });
    ro.observe(containerRef.current);

    chart.subscribeCrosshairMove((param) => {
      const point = param.seriesData.get(series) as { value?: number } | undefined;
      const date = formatDateFromTime(param.time);
      if (!point || typeof point.value !== 'number' || !date) {
        setTooltip(null);
        return;
      }
      setTooltip({ date, value: point.value });
    });

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      glowSeriesRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    const glowSeries = glowSeriesRef.current;
    const series = seriesRef.current;
    if (!chart || !series || !glowSeries) return;

    glowSeries.setData(data);
    series.setData(data);

    if (scale === 'log') {
      series.priceScale().applyOptions({ mode: PriceScaleMode.Logarithmic });
      glowSeries.priceScale().applyOptions({ mode: PriceScaleMode.Logarithmic });
    } else {
      series.priceScale().applyOptions({ mode: PriceScaleMode.Normal });
      glowSeries.priceScale().applyOptions({ mode: PriceScaleMode.Normal });
    }

    createSeriesMarkers(series, [
      {
        time: PIZZA_DAY_DATE as Time,
        position: 'aboveBar',
        color: '#ff9d4d',
        shape: 'circle',
        text: 'Pizza Day',
      },
    ]);

    chart.timeScale().fitContent();
  }, [data, scale]);

  return (
    <div className="chart-shell">
      <div className="chart-meta-row">
        <strong>{metricLabel}</strong>
        {tooltip ? (
          <span>
            {tooltip.date} · {tooltip.value.toLocaleString('en-US', { maximumFractionDigits: 6 })}
          </span>
        ) : (
          <span>Move cursor over chart</span>
        )}
      </div>

      <div ref={containerRef} className="chart-canvas" data-testid="pizza-chart" aria-label="Interactive Bitcoin pizza chart" />

      {isLoading && <p className="muted small">Loading chart data…</p>}
      {error && <p className="error-text">{error}</p>}
    </div>
  );
}
